import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';
import isEqual from 'react-fast-compare';

import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import {UseSelectedAnalysisItemOutput} from 'analysis-tool/ui/hooks/useSelectedAnalysisItem';
import {StepInitializationAnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  isStepAwareAnalysisItem,
  isViewStepConfigAnalysisItem,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {trackEvent} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER} from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import AddChartViewPopover from '../charting/AddChartViewPopover';
import AddViewButton from './AddViewButton';
import SidebarItem from './SidebarItem';
import {SidebarItemData} from './SidebarItemDatas';
import SidebarItemWithActions from './SidebarItemWithActions';
import ToggleAnalysisSidebarButton from './ToggleAnalysisSidebarButton';

const sidebarItemExpandedState = makeUseGlobalClientState<Record<string, boolean>>({});

/** @package */
export interface SidebarSectionItemsProps {
  analysis: PipelineGraphQL;
  sidebarItemDatas: Array<SidebarItemData>;
  selectedItemState: UseSelectedAnalysisItemOutput;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void;
  tier?: number;
  /**
   * The number of tiers to expand by default. Items at tiers less than this value are expanded.
   * Defaults to 1 (tier 0 items expanded).
   * Set to 0 to collapse all tiers by default.
   */
  defaultExpandedTier?: number;
  /**
   * Controls how child items are indented:
   * - `'margin'` (default): wraps children in a div with marginLeft (legacy sidebar).
   * - `'padding'`: passes indentLevel to SidebarItem so padding handles indent (new sidebar).
   *
   * TODO(BNCH-145708): Remove `'margin'` mode once the legacy sidebar is deleted.
   */
  indentMode?: 'margin' | 'padding';
}

/**
 * Render child `SidebarItem`s based on `sidebarItemDatas` and `selectedItemState`.
 *
 * If a `sidebarItemData` has `childSidebarItems`, recursively render `SidebarSectionItems` with
 * 20px of margin to render the next tier.
 *
 * ```
 * sidebarItemData
 *   childSidebarItem 1
 *   childSidebarItem 2
 *     child item of childSidebarItem 2
 * sidebarItemData
 * ```
 *
 * @param tier Internal-only prop for tracking the current tier when recursing over `childSidebarItems` for use
 * with the `key`.
 * @package
 */
export function SidebarSectionItems({
  analysis,
  sidebarItemDatas,
  selectedItemState,
  tier = 0,
  defaultExpandedTier = 1,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
  indentMode = 'margin',
}: SidebarSectionItemsProps): JSX.Element {
  const [expandedState, setExpandedState] = sidebarItemExpandedState.useGlobalClientState(analysis.id);
  const usePaddingIndent = indentMode === 'padding';

  function isItemExpanded(itemId: string, itemTier: number): boolean {
    if (expandedState[itemId] != null) {
      return expandedState[itemId];
    }
    // Items at tiers below defaultExpandedTier are expanded by default; others are collapsed.
    return itemTier < defaultExpandedTier;
  }

  function toggleItemExpanded(itemId: string, currentlyExpanded: boolean): void {
    setExpandedState({...expandedState, [itemId]: !currentlyExpanded});
  }

  return (
    <>
      {sidebarItemDatas.map(
        (
          {
            icon,
            title,
            mainViewItem,
            childSidebarItems = [],
            generateActionButtons,
            actionButtonOptions,
            disabled,
            inactive,
            subtitleText,
            isFlaggingRows = false,
            itemId,
          },
          index
        ) => {
          const hasChildren = childSidebarItems.length > 0;
          const isCollapsible = hasChildren && itemId != null;
          const isExpanded = isCollapsible ? isItemExpanded(itemId, tier) : true;

          const sidebarItemProps = {
            icon,
            isSelected:
              isEqual(selectedItemState.selectedItem, mainViewItem) ||
              (isViewStepConfigAnalysisItem(selectedItemState.selectedItem) &&
                isStepAwareAnalysisItem(mainViewItem) &&
                selectedItemState.selectedItem.stepId === mainViewItem.stepId),
            onSelect: () => {
              recordAnalysisAction('SIDEBAR_ITEM_SELECTED', {
                previousItem: selectedItemState.selectedItem,
                newItem: mainViewItem,
                tier,
                index,
              });
              trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.SELECTED_SIDEBAR_ITEM, {
                previousItem: selectedItemState.selectedItem,
                newItem: mainViewItem,
              });
              selectedItemState.onChangeSelectedItem(mainViewItem);
            },
            title,
            disabled,
            inactive,
            subtitleText,
            isFlaggingRows,
            dataTestAttrsKey: `${tier}-${index}`,
            isCollapsible,
            isExpanded,
            onToggleExpanded: isCollapsible
              ? () => {
                  toggleItemExpanded(itemId, isExpanded);
                }
              : undefined,
            // When using padding indent, indent via paddingLeft instead of
            // a wrapper marginLeft so hover/selection backgrounds span the full width.
            ...(usePaddingIndent ? {indentLevel: tier + 1} : {}),
          };

          // Use pre-computed action buttons from the data layer when available (new sidebar only).
          const actionButtons =
            usePaddingIndent && generateActionButtons != null
              ? generateActionButtons(
                  analysis,
                  onOpenStepInitializationView,
                  onChangeSelectedRightSidebarItem,
                  selectedItemState.onChangeSelectedItem
                )
              : undefined;
          const hasActionButtons = actionButtons != null && actionButtons.length > 0;

          return (
            <div key={itemId ?? `${tier}-${index}`}>
              {hasActionButtons ? (
                <SidebarItemWithActions {...sidebarItemProps} actionButtons={actionButtons} />
              ) : (
                <SidebarItem {...sidebarItemProps} />
              )}
              {hasChildren &&
                isExpanded &&
                (usePaddingIndent ? (
                  <SidebarSectionItems
                    analysis={analysis}
                    sidebarItemDatas={childSidebarItems}
                    selectedItemState={selectedItemState}
                    tier={tier + 1}
                    defaultExpandedTier={defaultExpandedTier}
                    onOpenStepInitializationView={onOpenStepInitializationView}
                    onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
                    indentMode={indentMode}
                  />
                ) : (
                  // TODO(BNCH-145708): Remove margin-based indentation once the legacy sidebar is deleted.
                  <div className={css(styles.childSidebarItemsWrapper)}>
                    <SidebarSectionItems
                      analysis={analysis}
                      sidebarItemDatas={childSidebarItems}
                      selectedItemState={selectedItemState}
                      tier={tier + 1}
                      defaultExpandedTier={defaultExpandedTier}
                      onOpenStepInitializationView={onOpenStepInitializationView}
                      onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
                      indentMode={indentMode}
                    />
                  </div>
                ))}
              {/* TODO(BNCH-145708): Remove legacy block-level AddViewButton once the legacy sidebar is deleted. */}
              {!usePaddingIndent &&
                actionButtonOptions &&
                (analysis.permissions.writable || analysis.permissions.unarchivable) &&
                isExpanded && (
                  <div className={css(styles.childSidebarItemsWrapper, styles.addViewButtonWrapper)}>
                    <AddChartViewPopover
                      analysis={analysis}
                      stepId={actionButtonOptions.stepDataReference.stepId}
                      dataFrameId={actionButtonOptions.dataFrameId}
                      onOpenStepInitializationView={onOpenStepInitializationView}
                      onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
                    >
                      {(openMenu) => (
                        <AddViewButton
                          disabledReason={
                            isAnalysisRunning(analysis)
                              ? 'Analysis is running'
                              : isArchived(analysis) && analysis.permissions.unarchivable
                                ? getArchivedTooltipMessage(analysis)
                                : null
                          }
                          onClick={openMenu}
                        />
                      )}
                    </AddChartViewPopover>
                  </div>
                )}
            </div>
          );
        }
      )}
    </>
  );
}

/** @package */
export interface SidebarSectionProps extends Omit<SidebarSectionItemsProps, 'tier'> {
  headerButton?: ReactNode;
  onToggleSidebar?: () => void;
  title: string;
}

/** @package */
export default function SidebarSection({
  headerButton,
  onToggleSidebar,
  title,
  ...sidebarSectionItemsProps
}: SidebarSectionProps): JSX.Element {
  return (
    <div role="region" aria-label={title}>
      <div
        className={css(styles.sectionHeader)}
        {...getTestProps({component: 'SidebarSection', element: 'header'})}
      >
        <div className={css(styles.sectionTitle)}>{title}</div>
        {headerButton}
        {onToggleSidebar && (
          <div className={css(styles.toggleSidebarButton)}>
            <ToggleAnalysisSidebarButton sidebarIsOpen={true} toggleSidebar={onToggleSidebar} />
          </div>
        )}
      </div>
      <SidebarSectionItems {...sidebarSectionItemsProps} />
    </div>
  );
}

const styles = StyleSheet.create({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    textTransform: 'uppercase',
    color: GRAY_DARKER,
    letterSpacing: 1,
  },
  childSidebarItemsWrapper: {
    marginLeft: 20,
  },
  toggleSidebarButton: {
    marginLeft: 'auto',
  },
  addViewButtonWrapper: {
    marginTop: 8,
    marginBottom: 12,
    // Padding to make the add icon aligned with other child items in the section.
    paddingLeft: 7,
  },
});
